import React, { useEffect, useState } from 'react';
import { Page } from '../types';

interface BookingProps {
  setPage: (page: Page) => void;
}

const Booking: React.FC<BookingProps> = ({ setPage }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Dynamically inject the Calendly script when the component mounts
    const script = document.createElement('script');
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.onload = () => setIsLoaded(true);
    document.body.appendChild(script);

    return () => {
      // Clean up the script when leaving the page to prevent memory leaks or conflicts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 dark:bg-white/5 text-primary dark:text-accent-400 text-[10px] font-black uppercase tracking-widest mb-6 border border-primary/10">
            <span className="material-symbols-rounded text-sm">event_available</span>
            Direct Consultation
          </div>
          <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4 animate-slide-up">
            Schedule Your <span className="text-primary italic">Strategy Session</span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto animate-slide-up [animation-delay:200ms]">
            Pick a time that works for you. Our senior compliance leads are ready to guide your business registration and tax strategy.
          </p>
        </div>

        <div className="relative group">
          {/* Decorative Background Glow */}
          <div className="absolute -inset-4 bg-primary/5 dark:bg-primary/20 blur-3xl rounded-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-1000 -z-10"></div>
          
          <div className="bg-white dark:bg-slate-900 rounded-[40px] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden animate-reveal [animation-delay:400ms] min-h-[700px] flex flex-col items-center justify-center">
            {!isLoaded && (
              <div className="flex flex-col items-center gap-4 py-20">
                <div className="w-12 h-12 border-4 border-primary/10 border-t-primary rounded-full animate-spin"></div>
                <p className="text-sm font-bold text-slate-400 animate-pulse">Syncing with Calendly...</p>
              </div>
            )}
            
            {/* Calendly inline widget begin */}
            <div 
              className="calendly-inline-widget w-full" 
              data-url="https://calendly.com/finrackbizsolutions/30min?hide_event_type_details=1&hide_gdpr_banner=1" 
              style={{ minWidth: '320px', height: '700px' }}
            ></div>
            {/* Calendly inline widget end */}
          </div>
        </div>

        <div className="mt-12 text-center flex flex-col items-center gap-6 animate-fade-in [animation-delay:800ms]">
          <div className="flex items-center gap-8 py-4 px-8 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
             <div className="flex items-center gap-2">
               <span className="material-symbols-rounded text-green-500">lock</span>
               <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Secure Booking</span>
             </div>
             <div className="w-px h-4 bg-slate-200 dark:bg-slate-700"></div>
             <div className="flex items-center gap-2">
               <span className="material-symbols-rounded text-primary">verified</span>
               <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Instant Confirmation</span>
             </div>
          </div>
          
          <button 
            onClick={() => setPage('home')}
            className="group flex items-center gap-2 text-slate-400 hover:text-primary transition-all font-bold text-sm"
          >
            <span className="material-symbols-rounded group-hover:-translate-x-1 transition-transform">arrow_back</span>
            Return to Homepage
          </button>
        </div>
      </div>
    </div>
  );
};

export default Booking;